import { Dispatch, forwardRef, SetStateAction, useCallback, useImperativeHandle, useState } from 'react';
import { connect } from 'react-redux';
import { Field, useField } from 'react-final-form';

import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { KnmOkveds } from 'KnmPackage/MainModule/components/KnmOkveds';
import { KnmSubjectNkoInfo } from 'KnmPackage/MainModule/components/KnmSubjectNkoInfo';
import {
  COUNTRY_BLOCK_ID,
  DOCUMENT_BLOCK_ID,
  getOkvedBlockId as makeOkvedBlockId,
  INN_LABEL_BLOCK_ID,
  KPP_BLOCK_ID,
  OGRN_LABEL_BLOCK_ID,
  OKVEDS_BLOCK_ID,
  ORGANIZATION_NAME_BLOCK_ID,
  RAFP_NZA_BLOCK_ID,
  SNILS_BLOCK_ID,
} from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { KnmSubjectItInfo } from 'KnmPackage/MainModule/components/KnmSubjectItInfo';
import { IKnmSubject248ItAccreditationResponse } from 'APIPackage/KnmModule/interfaces/IKnmSubject248ItAccreditationResponse';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { ErknmFieldMark } from 'KnmErknmPackage/MainModule/components/ErknmFieldMark';
import { KnmFiasAddressField } from 'KnmPackage/MainModule/components/KnmFiasAddressField';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { getDistrictSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { rafpNotFoundMessage } from 'KnmErknmPackage/ErknmSubjectModule/constants/erknmSubjectConstants';
import { getDecisionDataCreatedAt } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';

import { ICountry } from '../../../APIPackage/CatalogsModule/interfaces/ICountry';
import { ILegalEntityLookup } from '../../../APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { RafpRequestStatusTypeEnum } from '../../../APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { IKnmCheckSubjectResult } from '../../../APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { KnmSubjectTypesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { KnmSnilsField } from '../../../KnmPackage/MainModule/components/KnmSnilsField';
import { useKnmSetFnsLookupLoadingFlags } from '../../../KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmDateChangeControllingSubject,
  getKnmFormRef,
  getKnmIsChangeSubjectTypeFromActionsAvailable,
  getKnmIsNew,
  getKnmOldNameControllingSubject,
  getKnmOnSaveButtonClick,
  getTargetEntitySelectOptions,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from '../../../UIPackage/MainModule/components/TextareaField';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { IKnmPmForm } from '../../MainModule/interfaces/IKnmPmForm';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { InspectionResultFieldEnum } from '../../../APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import styles from './PmSubject.module.scss';
import {
  isFnsAddressFnsError,
  isOrganizationNameFnsError,
  isWorkAddressFnsError,
  renderFnsCheckMessage,
  renderFnsErvkEqualsError,
} from '../../../CorePackage/MainModule/common/renderFnsMessage';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { usePmErknmSubjectFields } from '../hooks/usePmErknmSubjectFields';
import { usePmErknmSubjectHandlers } from '../hooks/usePmErknmSubjectHandlers';
import { useControllingSubjectNameChangeText } from '../../../KnmPackage/MainModule/hooks/useKnmNameChangeHintText';
import { IOldNameControllingSubject } from '../../../APIPackage/KnmModule/interfaces/IOldNameControllingSubject';

export interface IPmSubjectItemProps {
  ervkId: string | undefined;
  checkSubjectResult?: IKnmCheckSubjectResult | null;
  getBlockId: (id: string) => string;
  inn: string | undefined;
  inspectionResults?: IInspectionResult[];
  isFiz: boolean | undefined;
  isPresentInItRegistry: boolean | undefined;
  itAccreditationResponse: IKnmSubject248ItAccreditationResponse | undefined;
  kpp: string | undefined;
  name?: string;
  ogrn: string | undefined;
  okveds: IKnmPmForm['okveds'] | undefined;
  organizationAutocomplete: IKnmPmForm['organizationAutocomplete'];
  rafpNza: string | undefined;
  smevItAccreditationRequestStatus: string | undefined;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  snils: string | undefined;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  subjectTypeLabel?: string;
  watchOnly: boolean;
  checkByInnMessage?: string;
  setCheckByInnMessage?: Dispatch<SetStateAction<string | undefined>>;
  isLinkedByImpossibility?: boolean;
}

interface IPmSubjectItemStateProps {
  formRef: IKnmFormRef | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
  isChangeSubjectTypeFromActionsAvailable: boolean;
  isNew: boolean;
  districtSelectOptions: IKnmDistrictSelectOption[];
  settings: IAdminSetting[];
  created: string | null | undefined;
  decisionDataCreated?: string | null;
  oldNameControllingSubject: IOldNameControllingSubject[] | null | undefined;
  dateReasonChangeControllingSubject: string | null | undefined;
}

export interface IPmSubjectItemRef {
  onClear: () => void;
}

const PmSubjectItemWithoutStore = forwardRef<IPmSubjectItemRef, IPmSubjectItemProps & IPmSubjectItemStateProps>(
  (
    {
      ervkId,
      checkSubjectResult,
      getBlockId,
      inn,
      inspectionResults,
      isFiz,
      isPresentInItRegistry,
      itAccreditationResponse,
      kpp,
      name: parentName,
      ogrn,
      okveds,
      organizationAutocomplete,
      rafpNza,
      smevItAccreditationRequestStatus,
      smevRequestStatus,
      snils,
      subjectType,
      subjectTypeLabel,
      watchOnly,
      formRef,
      subjectTypeOptions,
      checkByInnMessage,
      setCheckByInnMessage,
      isChangeSubjectTypeFromActionsAvailable,
      isNew,
      isLinkedByImpossibility,
      settings,
      districtSelectOptions,
      created,
      decisionDataCreated,
      oldNameControllingSubject,
      dateReasonChangeControllingSubject,
    },
    ref,
  ) => {
    const name = parentName ? `${parentName}.` : '';
    const nameWithoutDot = name.endsWith('.') ? name.slice(0, -1) : name;
    const subjectFieldNames = usePmErknmSubjectFields({ name: nameWithoutDot, isRemovePrefix: true });

    const organizationGuid: string | undefined = useField(`${name}organizationGuid`, {
      subscription: { value: true },
    }).input.value;
    const organizationNameValue: string | undefined = useField(subjectFieldNames.organizationName, {
      subscription: { value: true },
    }).input.value;

    const organizationNameChangeText = useControllingSubjectNameChangeText({
      type: 'SUBJECT',
      guid: organizationGuid,
      currentName: organizationNameValue,
      currentCount: 1,
      oldNames: oldNameControllingSubject,
      changeDate: dateReasonChangeControllingSubject,
    });

    const organizationNameChangeHint = organizationNameChangeText ? (
      <div>
        <Hint text={organizationNameChangeText} margin="top" />
      </div>
    ) : null;

    const subjectTypeBlockId = getBlockId(subjectFieldNames.subjectTypePm);
    const organizationNameBlockId = getBlockId(ORGANIZATION_NAME_BLOCK_ID);
    const countryBlockId = getBlockId(COUNTRY_BLOCK_ID);
    const documentBlockId = getBlockId(DOCUMENT_BLOCK_ID);
    const innLabelBlockId = getBlockId(INN_LABEL_BLOCK_ID);
    const kppBlockId = getBlockId(KPP_BLOCK_ID);
    const ogrnLabelBlockId = getBlockId(OGRN_LABEL_BLOCK_ID);
    const okvedsBlockId = getBlockId(OKVEDS_BLOCK_ID);
    const rafpNzaBlockId = getBlockId(RAFP_NZA_BLOCK_ID);
    const snilsBlockId = getBlockId(SNILS_BLOCK_ID);

    const getOkvedBlockId = useCallback((id: string) => getBlockId(makeOkvedBlockId(id)), [getBlockId]);

    const isRafpNzaSent = smevRequestStatus === RafpRequestStatusTypeEnum.REQUEST_SENT;
    const isRafpNzaNotFound = smevRequestStatus === RafpRequestStatusTypeEnum.NOT_FOUND;
    const isRafpNzaError = smevRequestStatus === RafpRequestStatusTypeEnum.ERROR_RETRY;
    const isFIZ = subjectType?.label === KnmSubjectTypesEnum.FIZ;
    const isUR = subjectType?.label === KnmSubjectTypesEnum.UR;
    const isIP = subjectType?.label === KnmSubjectTypesEnum.IP;
    const isIFIZ = subjectType?.label === KnmSubjectTypesEnum.IFIZ;
    const isFIUR = subjectType?.label === KnmSubjectTypesEnum.FIUR;
    const isIUR = subjectType?.label === KnmSubjectTypesEnum.IUR;
    const isLaterThanHideFields4129 = isLaterThanAdminSetting(
      created || decisionDataCreated,
      settings,
      AdminSettingCodesEnum.SETTING_HIDE_FIELDS_4_1_29,
    );

    const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();
    const [lookupData, setLookupData] = useState<ILegalEntityLookup | boolean | undefined>(undefined);

    const [rafpNzaNotFoundText, setRafpNzaNotFoundText] = useState<string | undefined>(rafpNotFoundMessage);

    const isShowAutoCompleteField = isUR || isIP;

    const {
      handleLegalEntityAutoCompleteSelect,
      handleAutoCompleteClear,
      onClear,
      subjectTypeChange,
      handleRafpNzaChange,
    } = usePmErknmSubjectHandlers({
      formRef,
      isChangeSubjectTypeFromActionsAvailable,
      isNew,
      subjectFieldNames,
      subjectType,
      isUR,
      isIP,
      name,
      setCheckByInnMessage,
      rafpNza,
      setRafpNzaNotFoundText,
      isRafpNzaNotFound,
      rafpNotFoundMessage,
      districtSelectOptions,
    });

    useImperativeHandle(
      ref,
      () => {
        return {
          onClear,
        };
      },
      [],
    );

    const normalizeFio = (value: string | undefined) => {
      if (!value) return value;

      if (isFIZ) {
        return value.replace(/[^а-яё\d- ]/gi, '');
      }
      if (isIFIZ) {
        return value.replace(/[^а-яёa-z-`' ]/gi, '');
      }

      return value;
    };

    const isRafpFilled = !!rafpNza && /^\d{11}$/.test(rafpNza);

    const saveKnmAndWaitFromRafpWarning =
      (isRafpFilled || (inn && kpp)) && !smevRequestStatus
        ? 'Сохраните КНМ и ожидайте загрузки информации из РАФП'
        : undefined;

    const waitFromRafpWarning = isRafpNzaSent ? 'Ожидайте загрузки информации из РАФП' : undefined;

    const rafpWarning = isFIUR ? saveKnmAndWaitFromRafpWarning || waitFromRafpWarning : undefined;

    const innOrSnilsWarning = isFiz && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
    const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
    const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

    const IROkveds = inspectionResults?.find(
      i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_OKVED,
    );
    const IRNko = inspectionResults?.find(
      i => i.inspectedField === InspectionResultFieldEnum.FOREIGN_AGENT_NKO_REGISTRY,
    );
    const IROrganizationName = inspectionResults?.find(
      i =>
        i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_NAME ||
        i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_ERVK_NAME,
    );
    const IRLocationAddress = inspectionResults?.find(
      i =>
        i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_ADDRESS ||
        i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_ERVK_ADDRESS,
    );
    const IRFactualAddress = inspectionResults?.find(
      i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_FACTUAL_ADDRESS,
    );
    const IRRafpNza = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.RAFP_REGISTRY);

    const lateFieldName = '/subject';

    const isFilledFromLink = formRef?.current.getState().values.isFilledFromLink;
    const isFieldEnabledFirstCase = !watchOnly && !isFilledFromLink;
    const isFieldEnabledSecondCase = isChangeSubjectTypeFromActionsAvailable && !ervkId;

    const isFieldEnabled = isFieldEnabledFirstCase || isFieldEnabledSecondCase || !watchOnly;

    const isInnDisabled =
      !isFieldEnabled || organizationAutocomplete || (isFIUR && !!rafpNza) || isLinkedByImpossibility;
    const innWarning =
      innOrSnilsWarning ||
      rafpWarning ||
      (!isFIZ &&
        !smevRequestStatus &&
        inn &&
        renderFnsCheckMessage(checkSubjectResult, lookupData, organizationAutocomplete));

    const isOkvedsWatchOnly = !isFieldEnabled || isLinkedByImpossibility === true;

    const isAutocompleteValue = useField(subjectFieldNames.organizationAutocomplete, { subscription: { value: true } })
      .input.value;

    const isOrganizationNameFieldEnabled = !isFieldEnabled && isFiz;

    return (
      <>
        <Field name={subjectFieldNames.checkSubjectResult}>{() => null}</Field>
        <Field name={subjectFieldNames.dateCreate}>{() => null}</Field>
        <Field name={subjectFieldNames.firstOrganizationName}>{() => null}</Field>
        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: subjectTypeLabel || '',
              field: subjectFieldNames.subjectTypePm,
              isPm: true,
            })}
            size="half"
            color="black"
            tooltip={
              <>
                <Tooltip
                  placement="right"
                  type="info"
                  content={
                    <div className="shared-tooltip-info-content">
                      Поле зависит от значения в следующем поле:
                      <ul>
                        <p>Контрольный (надзорный) орган</p>
                        <p>Вид контроля (надзора)</p>
                        <p>Основания проведения профилактических мероприятий</p>
                      </ul>
                    </div>
                  }
                >
                  <span className="shared-tooltip-info-icon" />
                </Tooltip>
                <KnmLateFieldsRemark
                  lateFieldPath={`${lateFieldName}/subjectType/title`}
                  fieldId={subjectTypeBlockId}
                />
              </>
            }
            id={subjectTypeBlockId}
          >
            <SelectInputField
              name={subjectFieldNames.subjectTypePm}
              options={subjectTypeOptions}
              size="large"
              onChange={subjectTypeChange}
              disabled={!isFieldEnabled || isLinkedByImpossibility}
            />
          </FieldBlock>
        </Row>
        {subjectType && (
          <>
            {!isIFIZ && !isIUR && (
              <Row margin="none">
                {(isUR || isIP) && (
                  <FieldBlock
                    label={getTitleFieldWithRedStar({
                      label: 'Основной государственный регистрационный номер (ОГРН)',
                      field: subjectFieldNames.ogrn,
                      isPm: true,
                    })}
                    size="half"
                    color="black"
                    id={ogrnLabelBlockId}
                    tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/ogrn`} fieldId={ogrnLabelBlockId} />}
                  />
                )}
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: 'Идентификационный номер налогоплательщика (ИНН)',
                    field: subjectFieldNames.inn,
                    isPm: true,
                  })}
                  size="half"
                  color="black"
                  id={innLabelBlockId}
                  tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/inn`} fieldId={innLabelBlockId} />}
                />
                {isFIZ && (
                  <FieldBlock
                    label={getTitleFieldWithRedStar({
                      label: 'Страховой номер индивидуального лицевого счета (СНИЛС)',
                      field: subjectFieldNames.snils,
                      isPm: true,
                    })}
                    size="half"
                    color="black"
                    id={snilsBlockId}
                    tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/snils`} fieldId={snilsBlockId} />}
                  />
                )}
                {isFIUR && (
                  <FieldBlock
                    label={getTitleFieldWithRedStar({
                      label: 'Номер записи об аккредитации в РАФП',
                      field: subjectFieldNames.rafpNza,
                      isPm: true,
                    })}
                    size="half"
                    color="black"
                    id={rafpNzaBlockId}
                    tooltip={
                      <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/rafpNza`} fieldId={rafpNzaBlockId} />
                    }
                  />
                )}
              </Row>
            )}
            {!isIFIZ && !isIUR && (
              <Row>
                {(isUR || isIP) && (
                  <FieldBlock size="half">
                    <KnmOgrnField
                      name={subjectFieldNames.ogrn}
                      clearable={organizationAutocomplete}
                      disabled={!isFieldEnabled || organizationAutocomplete || isLinkedByImpossibility}
                      ogrn={ogrn}
                      onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                      onClear={handleAutoCompleteClear}
                      onLoadingChange={setFnsLookupByOgrnLoadingFlag}
                    />
                  </FieldBlock>
                )}
                <FieldBlock size="half">
                  <KnmInnField
                    name={subjectFieldNames.inn}
                    clearable={organizationAutocomplete}
                    disabled={isInnDisabled}
                    inn={inn}
                    onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                    setLookupData={setLookupData}
                    onClear={handleAutoCompleteClear}
                    onLoadingChange={setFnsLookupByInnLoadingFlag}
                    isFiz={isFIZ}
                    warning={innWarning}
                    isShowAutoCompleteField={isShowAutoCompleteField}
                    setCheckByInnMessage={!ervkId ? setCheckByInnMessage : undefined}
                    ervkId={ervkId}
                    subjectType={subjectType}
                    checkSubjectResult={checkSubjectResult}
                  />
                  {isRafpNzaNotFound && inn && kpp && <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>}
                </FieldBlock>
                {isFIZ && (
                  <FieldBlock size="half">
                    <KnmSnilsField
                      name={subjectFieldNames.snils}
                      warning={innOrSnilsWarning}
                      disabled={!isFieldEnabled || isLinkedByImpossibility}
                    />
                  </FieldBlock>
                )}
                {isFIUR && (
                  <FieldBlock size="half">
                    <TextInputField
                      name={subjectFieldNames.rafpNza}
                      disabled={!!inn || !!kpp || (!isRafpNzaNotFound && isRafpNzaSent) || isLinkedByImpossibility}
                      warning={rafpWarning}
                      inspectionResult={IRRafpNza}
                      onChange={handleRafpNzaChange}
                    />
                    {isRafpNzaNotFound && !inn && !kpp && (
                      <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>
                    )}
                    {isRafpNzaError && (
                      <Hint
                        text="Запрашиваемые сведения не могут быть предоставлены в настоящий момент. Автоматический запрос
                        будет произведен через час"
                      />
                    )}
                  </FieldBlock>
                )}
              </Row>
            )}
            {isFIUR && (
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: 'Код причины постановки на учет (КПП)',
                    field: subjectFieldNames.kpp,
                    isPm: true,
                  })}
                  size="half"
                  color="black"
                  id={kppBlockId}
                  tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/kpp`} fieldId={kppBlockId} />}
                >
                  <TextInputField
                    name={subjectFieldNames.kpp}
                    disabled={!isFieldEnabled || !!rafpNza || isLinkedByImpossibility}
                    warning={rafpWarning}
                  />
                </FieldBlock>
              </Row>
            )}
            {(isFIZ || isIFIZ) && (
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: 'Фамилия, имя, отчество (при наличии)',
                    field: subjectFieldNames.organizationName,
                    isPm: true,
                  })}
                  color="black"
                  id={organizationNameBlockId}
                  tooltip={
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/organizationName`}
                      fieldId={organizationNameBlockId}
                    />
                  }
                >
                  <TextareaField
                    disabled={!isFieldEnabled || isLinkedByImpossibility}
                    name={subjectFieldNames.organizationName}
                    resize="none"
                    inputClassName={styles.OrganizationNameInput}
                    parse={normalizeFio}
                  />
                  {organizationNameChangeHint}
                </FieldBlock>
              </Row>
            )}
            {(isUR || isIP || isFIUR) && (
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: isFIUR ? 'Наименование организации' : 'Наименование проверяемого лица',
                    field: subjectFieldNames.organizationName,
                    isPm: true,
                  })}
                  color="black"
                  id={organizationNameBlockId}
                  tooltip={
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/organizationName`}
                      fieldId={organizationNameBlockId}
                    />
                  }
                >
                  <TextareaField
                    name={subjectFieldNames.organizationName}
                    resize="none"
                    inputClassName={styles.OrganizationNameInput}
                    clearable={isOrganizationNameFieldEnabled && organizationAutocomplete}
                    disabled={!isOrganizationNameFieldEnabled || isLinkedByImpossibility}
                    onClear={handleAutoCompleteClear}
                    inspectionResult={IROrganizationName}
                  />
                  {!smevRequestStatus &&
                    isOrganizationNameFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                  {organizationNameChangeHint}
                </FieldBlock>
              </Row>
            )}
            {isIUR && (
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: 'Наименование',
                    field: subjectFieldNames.organizationName,
                    isPm: true,
                  })}
                  color="black"
                  id={organizationNameBlockId}
                  tooltip={
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/organizationName`}
                      fieldId={organizationNameBlockId}
                    />
                  }
                >
                  <TextInputField
                    id={organizationNameBlockId}
                    name={subjectFieldNames.organizationName}
                    disabled={!isFieldEnabled || isLinkedByImpossibility}
                  />
                  {organizationNameChangeHint}
                </FieldBlock>
              </Row>
            )}
            {(isUR || isFIUR) && (
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: 'Адрес места нахождения',
                    field: subjectFieldNames.locationAddress,
                    isPm: true,
                  })}
                  color="black"
                  tooltip={
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/locationAddress`}
                      fieldId={subjectFieldNames.locationAddress}
                    />
                  }
                >
                  <KnmFiasAddressField
                    id={subjectFieldNames.locationAddress}
                    addressFieldPathWithPrefix={subjectFieldNames.locationAddress}
                    required
                    disabled={true}
                    formRef={formRef}
                    prefixNameNotInFias={`${name}location`}
                    manualAddressPlaceholder={
                      <>
                        Место
                        <ErknmFieldMark fieldName={`${name}locationmanualAddress`} />
                      </>
                    }
                    regionPlaceholder={
                      <>
                        Субъект РФ
                        <ErknmFieldMark fieldName={`${name}locationregion`} />
                      </>
                    }
                    inspectionResult={IRLocationAddress}
                    hideManualAddress={!!ervkId || !!isAutocompleteValue}
                    fieldPaths={{
                      notInFIAS: subjectFieldNames.locationnotInFIAS,
                      isErrorFiasValue: subjectFieldNames.locationisErrorFiasValue,
                    }}
                  />
                  {!smevRequestStatus &&
                    isFnsAddressFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                </FieldBlock>
              </Row>
            )}
            {isUR && !isLaterThanHideFields4129 && (
              <Row>
                <FieldBlock label="Место нахождения (осуществления деятельности) контролируемого лица" color="black">
                  <TextInputField
                    name={subjectFieldNames.workAddress}
                    disabled={!isFieldEnabled || !!isAutocompleteValue || !!ervkId}
                  />
                  {!smevRequestStatus &&
                    isWorkAddressFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                </FieldBlock>
              </Row>
            )}
            {isIP && !isLaterThanHideFields4129 && (
              <FieldBlock label="Место фактического осуществления деятельности" color="black">
                <TextInputField
                  name={subjectFieldNames.factualAddress}
                  inspectionResult={IRFactualAddress}
                  disabled={!isFieldEnabled || !!isAutocompleteValue || !!ervkId}
                />
              </FieldBlock>
            )}
            {isIFIZ && (
              <Row>
                <FieldBlock
                  label={<>Сведения о документе, удостоверяющем личность</>}
                  color="black"
                  id={documentBlockId}
                  tooltip={
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/document`}
                      fieldId={subjectFieldNames.document}
                    />
                  }
                >
                  <TextInputField
                    name={subjectFieldNames.document}
                    parse={parsePassport}
                    disabled={!isFieldEnabled || isLinkedByImpossibility}
                  />
                </FieldBlock>
              </Row>
            )}
            {(isIFIZ || isIUR) && (
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: isIFIZ
                      ? 'Гражданство (при наличии)'
                      : 'Государство, в котором указанное юридическое лицо зарегистрировано',
                    field: subjectFieldNames.country,
                    isPm: true,
                  })}
                  color="black"
                  id={countryBlockId}
                  tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/country`} fieldId={countryBlockId} />}
                >
                  <SelectInputFieldAutoOptions
                    name={subjectFieldNames.country}
                    isClearable={false}
                    size="medium"
                    optionName={CommonCatalogsNamesEnum.COUNTRIES}
                    transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                    disabled={!isFieldEnabled || isLinkedByImpossibility}
                  />
                </FieldBlock>
              </Row>
            )}
            {(isUR || isIP || isFIUR) && (
              <KnmOkveds
                name={subjectFieldNames.okveds}
                okveds={okveds}
                organizationAutocomplete={organizationAutocomplete}
                blockId={okvedsBlockId}
                getOkvedBlockId={getOkvedBlockId}
                subjectData={{ inn, ogrn }}
                watchOnly={isOkvedsWatchOnly}
                inspectionResult={IROkveds}
                checkSubjectResult={checkSubjectResult}
              />
            )}
            {isUR && (
              <KnmSubjectNkoInfo
                id=""
                name=""
                ogrn={ogrn}
                inspectionResult={IRNko}
                checkByInnMessage={checkByInnMessage}
                formRef={formRef}
                isShowMessages={false}
                ervkId={ervkId}
              />
            )}

            {/* Блок информации о наличии контролируемого лица в реестре IT-компаний */}
            {(isUR || isFIUR) && (
              <KnmSubjectItInfo
                inn={inn}
                inspectionResults={inspectionResults}
                isPresentInItRegistry={isPresentInItRegistry}
                itAccreditationResponse={itAccreditationResponse}
                smevItAccreditationRequestStatus={smevItAccreditationRequestStatus}
                checkByInnMessage={checkByInnMessage}
                isShowMessages={false}
                ervkId={ervkId}
              />
            )}
          </>
        )}
      </>
    );
  },
);

const mapStateToProps = (state: IRootState) => ({
  isChangeSubjectTypeFromActionsAvailable: getKnmIsChangeSubjectTypeFromActionsAvailable(state),
  subjectTypeOptions: getTargetEntitySelectOptions(state),
  onSaveButtonClick: getKnmOnSaveButtonClick(state),
  formRef: getKnmFormRef(state),
  isNew: getKnmIsNew(state),
  districtSelectOptions: getDistrictSelectOptions(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
  decisionDataCreated: getDecisionDataCreatedAt(state),
  oldNameControllingSubject: getKnmOldNameControllingSubject(state),
  dateReasonChangeControllingSubject: getKnmDateChangeControllingSubject(state),
});

export const PmSubjectItem = connect(mapStateToProps, null, null, { forwardRef: true })(PmSubjectItemWithoutStore);
